/**
 * Syllabus overview section for level pages.
 *
 * What: A prospectus spread — a sticky left rail carrying the section identity
 * and the derived chapter counts, beside a document card holding the lede, the
 * chapter list, and the exam-alignment note.
 * Why: A syllabus is a document, so it is set as one. The intro used to render
 * as centered muted type in a 680px column, which put six to ten gray lines
 * between the heading and the content; as a left-aligned lede inside the card
 * it reads at document measure, and the chapters run two-up to halve the scroll.
 * When: Update when the syllabus content shape or the level-page section rhythm
 * changes.
 */
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import LandingSection from '@/components/landing/LandingSection';
import { colors, radii, shadows } from '@/components/landing/landingTokens';

interface TopicGroup {
  category: string;
  items: string[];
}

type Topic = string | TopicGroup;

export interface SyllabusSectionContent {
  title: string;
  /** Optional uppercase label above the heading, e.g. "Syllabus". */
  eyebrow?: string;
  body?: string;
  /** Optional: a level may describe its coverage in prose with no topic list. */
  topics?: Topic[];
  note?: string;
}

interface SyllabusSectionProps {
  content: SyllabusSectionContent;
}

interface RailStat {
  label: string;
  value: string;
}

/**
 * The chapter-numbered categories the primary and G2/G3 stream pages carry.
 * Splitting the index off the title lets it set as an index line; categories
 * that are strands, terms or years (PSLE, IP, IB) keep their plain title.
 */
const CHAPTER_CATEGORY = /^(Chapter\s+\d+)\s*:\s*(.+)$/u;

function isTopicGroup(topic: Topic): topic is TopicGroup {
  return (
    typeof topic === 'object' &&
    topic !== null &&
    'category' in topic &&
    'items' in topic
  );
}

function splitCategory(category: string): {
  index: string | null;
  title: string;
} {
  const match = CHAPTER_CATEGORY.exec(category);
  return match
    ? { index: match[1], title: match[2] }
    : { index: null, title: category };
}

/**
 * Counts for the rail, derived from the data — never authored.
 *
 * Only a page whose every category is a numbered chapter gets them: "chapters"
 * is the wrong vocabulary for a page grouped by strand, term or year, and a
 * partly-chaptered list would count two different things.
 */
function deriveStats(topics: readonly Topic[]): RailStat[] {
  const groups = topics.filter(isTopicGroup);
  if (groups.length === 0 || groups.length !== topics.length) return [];
  if (!groups.every(group => CHAPTER_CATEGORY.test(group.category))) return [];

  const subTopicCount = groups.reduce(
    (total, group) => total + group.items.length,
    0
  );

  return [
    { label: 'Chapters', value: String(groups.length) },
    { label: 'Sub-topics', value: String(subTopicCount) },
  ];
}

function TopicItem({ item }: { item: string }) {
  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        minWidth: 0,
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 6,
          height: 6,
          mt: '8px',
          flexShrink: 0,
          borderRadius: radii.pill,
          backgroundColor: alpha(colors.accentOrange, 0.9),
        }}
      />
      <Box
        component="span"
        sx={{
          fontSize: 15,
          lineHeight: 1.55,
          color: colors.ink,
          minWidth: 0,
          overflowWrap: 'break-word',
        }}
      >
        {item}
      </Box>
    </Box>
  );
}

function SyllabusSection({ content }: SyllabusSectionProps) {
  const titleId = 'syllabus-title';
  const topics = content.topics ?? [];
  const stats = deriveStats(topics);

  return (
    <LandingSection id="syllabus" tone="transparent" ariaLabelledby={titleId}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 4fr) minmax(0, 8fr)',
          },
          gap: { xs: 3.25, md: 7 },
          alignItems: 'start',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Sticky at md+: the reader keeps the section identity and the counts
            while scrolling fourteen chapters. The marketing top bar is
            absolute, not fixed, so a small offset is all it needs to clear. */}
        <Box
          component="header"
          sx={{
            minWidth: 0,
            position: { md: 'sticky' },
            top: { md: '24px' },
          }}
        >
          {content.eyebrow && (
            <Typography
              component="p"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.09em',
                fontWeight: 800,
                fontSize: 11,
                color: colors.accentCobalt,
              }}
            >
              {content.eyebrow}
            </Typography>
          )}

          <Typography
            id={titleId}
            component="h2"
            sx={{
              mt: content.eyebrow ? '12px' : 0,
              fontWeight: 800,
              fontSize: { xs: 24, sm: 28 },
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              color: colors.ink,
              overflowWrap: 'break-word',
            }}
          >
            {content.title}
          </Typography>

          {stats.length > 0 && (
            <Box
              sx={{
                mt: { xs: '22px', md: '30px' },
                // Hairline rows at md+; a wrapping row of pills below it.
                display: { xs: 'flex', md: 'block' },
                flexWrap: 'wrap',
                gap: { xs: '10px', md: 0 },
                borderTop: { xs: 'none', md: `1px solid ${colors.border}` },
              }}
            >
              {stats.map(stat => (
                <Box
                  key={stat.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: { xs: 'flex-start', md: 'space-between' },
                    gap: { xs: '9px', md: 0 },
                    px: { xs: '16px', md: '2px' },
                    py: { xs: '8px', md: '13px' },
                    borderStyle: 'solid',
                    borderColor: colors.border,
                    borderWidth: { xs: '1px', md: '0 0 1px' },
                    borderRadius: { xs: radii.pill, md: 0 },
                    backgroundColor: {
                      xs: colors.paper,
                      md: 'transparent',
                    },
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      letterSpacing: '0.01em',
                      color: colors.inkMuted,
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: 15, md: 18 },
                      fontWeight: 800,
                      color: colors.ink,
                      fontFeatureSettings: "'tnum'",
                    }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Paper
          elevation={0}
          sx={{
            minWidth: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
            px: { xs: '24px', md: '48px' },
            py: { xs: '28px', md: '44px' },
            borderRadius: { xs: radii.md, md: radii.lg },
            backgroundColor: colors.paper,
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
          }}
        >
          {content.body && (
            <Typography
              component="p"
              sx={{
                fontSize: { xs: 15.5, sm: 17 },
                lineHeight: 1.75,
                letterSpacing: '-0.004em',
                color: colors.ink,
                maxWidth: '62ch',
                overflowWrap: 'break-word',
              }}
            >
              {content.body}
            </Typography>
          )}

          {content.body && topics.length > 0 && (
            <Box
              aria-hidden
              sx={{
                height: '1px',
                my: '34px',
                backgroundColor: colors.border,
              }}
            />
          )}

          {topics.length > 0 && (
            <Stack spacing={4} sx={{ minWidth: 0 }}>
              {topics.map((topic, idx) => {
                if (!isTopicGroup(topic)) {
                  return (
                    <Box
                      key={idx}
                      component="ul"
                      sx={{ listStyle: 'none', m: 0, p: 0, minWidth: 0 }}
                    >
                      <TopicItem item={topic} />
                    </Box>
                  );
                }

                const { index, title } = splitCategory(topic.category);

                return (
                  <Box key={idx} sx={{ minWidth: 0 }}>
                    <Typography component="h3">
                      {index && (
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            lineHeight: 1.3,
                            textTransform: 'uppercase',
                            color: colors.accentCobalt,
                            fontFeatureSettings: "'tnum'",
                          }}
                        >
                          {index}
                        </Box>
                      )}
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          mt: index ? '5px' : 0,
                          fontSize: 17,
                          fontWeight: 800,
                          letterSpacing: '-0.01em',
                          lineHeight: 1.35,
                          color: colors.ink,
                          overflowWrap: 'break-word',
                        }}
                      >
                        {title}
                      </Box>
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        listStyle: 'none',
                        m: 0,
                        p: 0,
                        mt: '13px',
                        minWidth: 0,
                        display: 'grid',
                        // `auto-fit`, not `auto-fill`: a group carrying one
                        // item (the secondary hubs describe a whole stream in
                        // a single paragraph) collapses the empty track and
                        // reads across the full measure instead of wrapping
                        // ten times down a 250px column.
                        gridTemplateColumns:
                          'repeat(auto-fit, minmax(250px, 1fr))',
                        rowGap: 1.25,
                        columnGap: { xs: 4, md: 5.5 },
                      }}
                    >
                      {topic.items.map((item, itemIdx) => (
                        <TopicItem key={itemIdx} item={item} />
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}

          {content.note && (
            <Box
              sx={{
                mt: 4.5,
                pt: 3.25,
                borderTop: `1px solid ${colors.border}`,
                minWidth: 0,
              }}
            >
              <Typography
                component="span"
                sx={{
                  display: 'block',
                  mb: 1,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: colors.inkSubtle,
                }}
              >
                Note
              </Typography>
              <Typography
                component="p"
                sx={{
                  fontSize: 14.5,
                  lineHeight: 1.7,
                  color: colors.inkMuted,
                  maxWidth: '72ch',
                  overflowWrap: 'break-word',
                }}
              >
                {content.note}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </LandingSection>
  );
}

export default SyllabusSection;
